import  { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { FiImage, FiVideo, FiFile, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import Loading from '../components/Loading';
const MediaItem = ({ media, onClick}) => {


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
      onClick={onClick}
    >
      {media.mediaType === 'image' ? (
        <img
          src={media.url}
          alt={media.title || 'Gallery image'}
          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      ) : media.mediaType === 'video' ? (
        <div className="relative w-full h-64 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <video
            src={media.url}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <FiVideo className="text-white text-4xl" />
          </div>
        </div>
      ) : (
        <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center p-4">
          <FiFile className="text-6xl text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center truncate w-full px-2">
            {media.title || 'Document'}
          </p>
        </div>
      )}
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
        <div className="text-white">
          <h3 className="font-medium text-sm truncate">{media.title || 'Untitled'}</h3>
          <p className="text-xs text-gray-200">
            {media.mediaType.charAt(0).toUpperCase() + media.mediaType.slice(1)}
            {media.size && ` • ${(media.size / 1024 / 1024).toFixed(1)} MB`}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const MediaViewer = ({ media, onClose, onNext, onPrev, hasNext, hasPrev }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { darkMode } = useTheme();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [media]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
      >
        <FiX className="w-8 h-8" />
      </button>

      {hasPrev && (
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors z-10"
        >
          <FiChevronLeft className="w-8 h-8" />
        </button>
      )}

      <div className="relative w-full max-w-4xl max-h-[90vh] flex items-center justify-center">
        {isLoading && (
          <Loading />
        )}
        
        <AnimatePresence mode="wait">
          <motion.div
            key={media._id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`${!isLoading ? 'opacity-100' : 'opacity-0'}`}
          >
            {media.mediaType === 'image' ? (
              <img
                src={media.url}
                alt={media.title || 'Gallery image'}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                onLoad={() => setIsLoading(false)}
              />
            ) : media.mediaType === 'video' ? (
              <video
                src={media.url}
                className="max-w-full max-h-[80vh] rounded-lg"
                controls
                autoPlay
                onCanPlay={() => setIsLoading(false)}
              />
            ) : (
              <div className={`p-8 rounded-lg max-w-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-center">
                  <FiFile className="mx-auto w-16 h-16 text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{media.title || 'Document'}</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    This is a {media.mediaType} file. Click the button below to download.
                  </p>
                  <a
                    href={media.url}
                    download
                    className="inline-flex items-center px-6 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download File
                  </a>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {hasNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors z-10"
        >
          <FiChevronRight className="w-8 h-8" />
        </button>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
      </div>
    </div>
  );
};

const PhotoGallery = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { darkMode } = useTheme();

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setLoading(true);
        const response = await api.get('/media/all');
        setMedia(response.data.media || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching media:', err);
        setError('Failed to load media. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filter === 'all' || item.mediaType === filter;
      return matchesSearch && matchesFilter;
    });
  }, [media, filter, searchQuery]);

  const openMedia = (index) => {
    setSelectedIndex(index);
    setSelectedMedia(filteredMedia[index]);
  };

  const navigateMedia = (direction) => {
    const newIndex = direction === 'next' ? selectedIndex + 1 : selectedIndex - 1;
    if (newIndex >= 0 && newIndex < filteredMedia.length) {
      setSelectedIndex(newIndex);
      setSelectedMedia(filteredMedia[newIndex]);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-6 max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Error Loading Gallery</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-16 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-8 -mt-8 ${darkMode ? 'bg-gray-800' : 'bg-white shadow-sm'} mb-8`}>
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Media Gallery</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {filteredMedia.length} {filteredMedia.length === 1 ? 'item' : 'items'} in gallery
          </p>
          
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full px-4 py-2 pl-10 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Filter:</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className={`px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Media</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="document">Documents</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Gallery Grid */}
      <div className="container mx-auto px-4">
        {filteredMedia.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-4 text-gray-300 dark:text-gray-600">
              <FiImage className="w-full h-full" />
            </div>
            <h3 className="text-xl font-medium mb-2">No media found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery
                ? 'No items match your search. Try a different term.'
                : filter !== 'all'
                ? `No ${filter}s found in the gallery.`
                : 'The gallery is empty. Upload some media to get started.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredMedia.map((item, index) => (
              <MediaItem
                key={item._id}
                media={item}
                onClick={() => openMedia(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Media Viewer Modal */}
      <AnimatePresence>
        {selectedMedia && (
          <MediaViewer
            media={selectedMedia}
            onClose={() => setSelectedMedia(null)}
            onNext={() => navigateMedia('next')}
            onPrev={() => navigateMedia('prev')}
            hasNext={selectedIndex < filteredMedia.length - 1}
            hasPrev={selectedIndex > 0}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Add PropTypes validation
MediaItem.propTypes = {
  media: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string.isRequired,
    mediaType: PropTypes.oneOf(['image', 'video', 'document']).isRequired,
    size: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

MediaViewer.propTypes = {
  media: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string.isRequired,
    mediaType: PropTypes.oneOf(['image', 'video', 'document']).isRequired,
  }),
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  hasNext: PropTypes.bool.isRequired,
  hasPrev: PropTypes.bool.isRequired,
};


export default PhotoGallery;
